import {
  BlockReasonSchema,
  BreBFaultModeSchema,
  CreditLineDetailSchema,
  CreditLineSummarySchema,
  CreditMovementSchema,
  CreditPaymentSchema,
  CustomerStatusSchema,
  DecisionPolicySchema,
  DelinquencyPolicySchema,
  DisputePolicySchema,
  DocumentTypeSchema,
  DrawdownSchema,
  FundingConfigSchema,
  FundingModeSchema,
  LimitRuleSchema,
  type LimitRule,
  MoneySchema,
  OriginationPolicySchema,
  PaymentChannelSchema,
  PaymentIntentSchema,
  PaymentIntentStatusSchema,
  ReasonCodeSchema,
  ReversalPolicySchema,
  SagaStepSchema,
  SagaStepStatusSchema,
  StatementSchema,
  TimestampSchema,
  UsuryRateSchema,
  AgentSettingsSchema,
  CreditProductConfigSchema,
  paginatedSchema,
} from '@paguz/contracts';
import { z } from 'zod';

/**
 * Esquemas de vista de `/v1/admin/*` que aún **no existen** en `@paguz/contracts`
 * (ver "Solicitudes" del resumen de WS-CON: moverlos a `packages/contracts/src/admin/`).
 * Reutilizan los esquemas públicos de contracts; aquí solo se agregan los datos de operación
 * (saga detallada, llamadas externas, auditoría, versiones de configuración).
 */

// ── Transacciones ─────────────────────────────────────────────────────────
export const ExternalSystemSchema = z.enum([
  'PAGUZ',
  'CREDIT_CORE',
  'DEPOSIT_CORE',
  'BREB',
  'NOTIFICATIONS',
]);
export type ExternalSystem = z.infer<typeof ExternalSystemSchema>;

/** Intento de un paso de la saga (`payment_saga_steps`). */
export const SagaStepDetailSchema = z.object({
  id: z.string(),
  step: SagaStepSchema,
  status: SagaStepStatusSchema,
  attempt: z.int().min(1),
  system: ExternalSystemSchema,
  adapter: z.string(),
  startedAt: TimestampSchema,
  finishedAt: TimestampSchema.nullable(),
  durationMs: z.int().min(0).nullable(),
  /** Código crudo del sistema externo (p. ej. `AC04`, `RJCT`), ya saneado. */
  externalCode: z.string().nullable(),
  reasonCode: ReasonCodeSchema.nullable(),
  /** Paso idempotente: se puede reintentar con la misma llave. */
  idempotent: z.boolean(),
  /** Paso de compensación (RELEASE, REVERSE_FUNDING). */
  compensation: z.boolean(),
  externalCallIds: z.array(z.string()),
});
export type SagaStepDetail = z.infer<typeof SagaStepDetailSchema>;

/** `integration.external_calls`: request/response **saneados** (sin PII ni secretos). */
export const ExternalCallSchema = z.object({
  id: z.string(),
  system: ExternalSystemSchema,
  adapter: z.string(),
  operation: z.string(),
  method: z.string(),
  endpoint: z.string(),
  httpStatus: z.int().nullable(),
  latencyMs: z.int().min(0),
  startedAt: TimestampSchema,
  outcome: z.enum(['OK', 'ERROR', 'TIMEOUT']),
  request: z.unknown(),
  response: z.unknown(),
});
export type ExternalCall = z.infer<typeof ExternalCallSchema>;

export const WebhookDeliveryStatusSchema = z.enum([
  'DELIVERED',
  'PENDING',
  'RETRYING',
  'FAILED',
  'NOT_SUBSCRIBED',
]);
export const EmittedEventSchema = z.object({
  id: z.string(),
  type: z.string(),
  occurredAt: TimestampSchema,
  webhook: z.object({
    status: WebhookDeliveryStatusSchema,
    attempts: z.int().min(0),
    lastAttemptAt: TimestampSchema.nullable(),
    httpStatus: z.int().nullable(),
  }),
});
export type EmittedEvent = z.infer<typeof EmittedEventSchema>;

export const BrebStatusSchema = z.enum([
  'UNKNOWN',
  'PENDING',
  'COMPLETED',
  'REJECTED',
  'NOT_FOUND',
]);
export type BrebStatus = z.infer<typeof BrebStatusSchema>;

export const AdminCustomerRefSchema = z.object({
  id: z.string(),
  fullName: z.string(),
  maskedDocument: z.string(),
  maskedPhone: z.string(),
});

export const AdminPaymentIntentSummarySchema = z.object({
  id: z.string(),
  status: PaymentIntentStatusSchema,
  customer: AdminCustomerRefSchema,
  payeeName: z.string(),
  payeeAlias: z.string(),
  amount: MoneySchema,
  installments: z.int(),
  channel: PaymentChannelSchema,
  fundingMode: FundingModeSchema,
  reasonCode: ReasonCodeSchema.nullable(),
  endToEndId: z.string().nullable(),
  createdAt: TimestampSchema,
});
export type AdminPaymentIntentSummary = z.infer<typeof AdminPaymentIntentSummarySchema>;
export const AdminPaymentIntentPageSchema = paginatedSchema(AdminPaymentIntentSummarySchema);

export const AdminPaymentIntentDetailSchema = z.object({
  intent: PaymentIntentSchema,
  customer: AdminCustomerRefSchema,
  authorizationId: z.string().nullable(),
  traceId: z.string(),
  saga: z.array(SagaStepDetailSchema),
  externalCalls: z.array(ExternalCallSchema),
  events: z.array(EmittedEventSchema),
  /** Último estado consultado en Bre-B (bloquea forzar compensación si es `COMPLETED`). */
  breb: z.object({ status: BrebStatusSchema, checkedAt: TimestampSchema.nullable() }),
  /** Excepción abierta (conciliación / posteo) que el operador puede marcar resuelta. */
  openException: z
    .object({ id: z.string(), type: z.string(), createdAt: TimestampSchema })
    .nullable(),
});
export type AdminPaymentIntentDetail = z.infer<typeof AdminPaymentIntentDetailSchema>;

export const AdminPaymentIntentQuerySchema = z.object({
  q: z.string().optional(),
  status: PaymentIntentStatusSchema.optional(),
  reasonCode: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  minAmount: z.string().optional(),
  maxAmount: z.string().optional(),
  cursor: z.string().optional(),
});
export type AdminPaymentIntentQuery = z.infer<typeof AdminPaymentIntentQuerySchema>;

export const OperatorActionResultSchema = z.object({
  detail: AdminPaymentIntentDetailSchema,
  message: z.string(),
});

// ── Clientes y líneas ─────────────────────────────────────────────────────
export const AdminCustomerSummarySchema = z.object({
  id: z.string(),
  fullName: z.string(),
  documentType: DocumentTypeSchema,
  maskedDocument: z.string(),
  maskedPhone: z.string(),
  businessName: z.string().nullable(),
  city: z.string().nullable(),
  status: CustomerStatusSchema,
  lineIds: z.array(z.string()),
  activeApplication: z.object({ id: z.string(), phase: z.string(), status: z.string() }).nullable(),
});
export type AdminCustomerSummary = z.infer<typeof AdminCustomerSummarySchema>;
export const AdminCustomerPageSchema = paginatedSchema(AdminCustomerSummarySchema);

export const AdminCustomerDetailSchema = AdminCustomerSummarySchema.extend({
  email: z.string().nullable(),
  createdAt: TimestampSchema,
  linkedAccounts: z.array(
    z.object({
      id: z.string(),
      participantName: z.string(),
      accountType: z.string(),
      maskedNumber: z.string(),
      isDefault: z.boolean(),
    }),
  ),
  lines: z.array(CreditLineSummarySchema),
  applications: z.array(
    z.object({
      id: z.string(),
      phase: z.string(),
      status: z.string(),
      requestedAmount: MoneySchema,
      createdAt: TimestampSchema,
    }),
  ),
  frequentPayees: z.array(
    z.object({ name: z.string(), nickname: z.string().nullable(), count: z.int() }),
  ),
  recentPayments: z.array(AdminPaymentIntentSummarySchema),
});
export type AdminCustomerDetail = z.infer<typeof AdminCustomerDetailSchema>;

export const AdminLineDetailSchema = z.object({
  line: CreditLineDetailSchema,
  customer: AdminCustomerRefSchema,
  drawdowns: z.array(DrawdownSchema.extend({ paymentIntentId: z.string().nullable() })),
  statements: z.array(StatementSchema),
  payments: z.array(CreditPaymentSchema),
  /** Solo ADMIN/AUDITOR (el servidor devuelve `null` al resto). */
  movements: z.array(CreditMovementSchema).nullable(),
});
export type AdminLineDetail = z.infer<typeof AdminLineDetailSchema>;

export const LineStatusRequestSchema = z.object({
  action: z.enum(['BLOCK', 'UNBLOCK']),
  blockReason: BlockReasonSchema.optional(),
  reason: z.string().trim().min(5).max(1000),
});
export type LineStatusRequest = z.infer<typeof LineStatusRequestSchema>;

// ── Configuración versionada ──────────────────────────────────────────────
export const ConfigVersionSchema = z.object({
  version: z.int().min(1),
  effectiveFrom: TimestampSchema,
  changedBy: z.string(),
  reason: z.string(),
});
export type ConfigVersion = z.infer<typeof ConfigVersionSchema>;

export function versionedSchema<T extends z.ZodType>(value: T) {
  return z.object({
    value,
    version: z.int().min(1),
    effectiveFrom: TimestampSchema,
    updatedBy: z.string(),
    history: z.array(ConfigVersionSchema),
  });
}
export interface Versioned<T> {
  value: T;
  version: number;
  effectiveFrom: string;
  updatedBy: string;
  history: ConfigVersion[];
}

export const ReversalSettingsSchema = z.object({
  reversalPolicy: ReversalPolicySchema,
  disputePolicy: DisputePolicySchema,
});
export type ReversalSettings = z.infer<typeof ReversalSettingsSchema>;

export const LimitsSettingsSchema = z.object({ rules: z.array(LimitRuleSchema) });

export const SETTINGS = {
  product: CreditProductConfigSchema,
  decisionPolicy: DecisionPolicySchema,
  reversal: ReversalSettingsSchema,
  delinquencyPolicy: DelinquencyPolicySchema,
  funding: FundingConfigSchema,
  originationPolicy: OriginationPolicySchema,
  limits: LimitsSettingsSchema,
  ai: AgentSettingsSchema,
} as const;
export type SettingKey = keyof typeof SETTINGS;
export type SettingValue<K extends SettingKey> = z.infer<(typeof SETTINGS)[K]>;

/** Rutas de `/v1/admin/*` por sección de configuración. */
export const SETTING_PATHS: Readonly<Record<SettingKey, string>> = {
  product: '/v1/admin/products/MICRO_ROTATIVO',
  decisionPolicy: '/v1/admin/settings/decision-policy',
  reversal: '/v1/admin/settings/reversal-policy',
  delinquencyPolicy: '/v1/admin/settings/delinquency-policy',
  funding: '/v1/admin/settings/funding',
  originationPolicy: '/v1/admin/settings/origination-policy',
  limits: '/v1/admin/settings/limits',
  ai: '/v1/admin/settings/ai',
};

/** Sección guardada tal cual en `tenant.settings` (la API acepta el valor + `reason`). */
const RAW_SECTION = {
  read: (raw: unknown) => raw,
  write: (value: unknown, reason: string) => ({ ...(value as object), reason }),
};

/**
 * Secciones que la API real publica en `backoffice` (`/admin/settings/{limits,funding,
 * decision-policy,origination-policy,delinquency-policy,ai}`): sin envoltura de versión y con su
 * propio cuerpo. La consola las adapta a `Versioned` con `version: 0` (el historial vive en la
 * auditoría). Producto y reversión siguen el formato versionado (pendiente en la API; hoy MSW).
 */
export const RAW_SETTING_WIRE: Partial<
  Record<
    SettingKey,
    { read: (raw: unknown) => unknown; write: (value: unknown, reason: string) => unknown }
  >
> = {
  limits: {
    read: (raw) => ({ rules: (raw as { data?: unknown }).data }),
    write: (value, reason) => ({
      rules: (value as { rules: LimitRule[] }).rules.map(({ id: _id, ...rule }) => rule),
      reason,
    }),
  },
  funding: RAW_SECTION,
  decisionPolicy: RAW_SECTION,
  originationPolicy: RAW_SECTION,
  delinquencyPolicy: RAW_SECTION,
  ai: RAW_SECTION,
};

export function settingResponseSchema<K extends SettingKey>(
  key: K,
): z.ZodType<Versioned<SettingValue<K>>> {
  const versioned = versionedSchema(SETTINGS[key]);
  const wire = RAW_SETTING_WIRE[key];
  const schema = wire
    ? z.union([
        versioned,
        z
          .unknown()
          .transform((raw) => wire.read(raw))
          .pipe(SETTINGS[key] as unknown as z.ZodType<SettingValue<K>>)
          .transform((value: SettingValue<K>) => ({
            value,
            version: 0,
            effectiveFrom: '',
            updatedBy: 'API',
            history: [],
          })),
      ])
    : versioned;
  return schema as unknown as z.ZodType<Versioned<SettingValue<K>>>;
}

export const UsuryRateListSchema = z.object({ data: z.array(UsuryRateSchema) });

export const DecisionPolicyPreviewSchema = z.object({
  total: z.int(),
  autoApproved: z.int(),
  autoRejected: z.int(),
  toReview: z.int(),
  windowLabel: z.string(),
  samples: z.array(
    z.object({
      applicationId: z.string(),
      applicant: z.string(),
      suggestedLimit: MoneySchema,
      score: z.int(),
      alerts: z.array(z.string()),
      outcome: z.enum(['AUTO_APPROVE', 'AUTO_REJECT', 'REVIEW']),
      why: z.string(),
    }),
  ),
});
export type DecisionPolicyPreview = z.infer<typeof DecisionPolicyPreviewSchema>;

// ── Auditoría ─────────────────────────────────────────────────────────────
export const AuditActorTypeSchema = z.enum(['USER', 'CUSTOMER', 'AGENT', 'SYSTEM']);
export const AuditEntrySchema = z.object({
  id: z.string(),
  seq: z.int().min(1),
  at: TimestampSchema,
  actor: z.object({ type: AuditActorTypeSchema, id: z.string(), name: z.string() }),
  action: z.string(),
  entityType: z.string(),
  entityId: z.string().nullable(),
  reason: z.string().nullable(),
  ip: z.string().nullable(),
  device: z.string().nullable(),
  before: z.unknown().nullable(),
  after: z.unknown().nullable(),
  traceId: z.string().nullable(),
  hash: z.string(),
  prevHash: z.string().nullable(),
});
export type AuditEntry = z.infer<typeof AuditEntrySchema>;
export const AuditPageSchema = paginatedSchema(AuditEntrySchema);

export const AuditQuerySchema = z.object({
  actor: z.string().optional(),
  actorId: z.string().optional(),
  action: z.string().optional(),
  entity: z.string().optional(),
  traceId: z.string().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  cursor: z.string().optional(),
});
export type AuditQuery = z.infer<typeof AuditQuerySchema>;

export const AuditVerifyResultSchema = z.object({
  status: z.enum(['INTACT', 'BROKEN']),
  checked: z.int(),
  from: TimestampSchema.nullable(),
  to: TimestampSchema.nullable(),
  firstInvalid: z.object({ id: z.string(), seq: z.int(), at: TimestampSchema }).nullable(),
});
export type AuditVerifyResult = z.infer<typeof AuditVerifyResultSchema>;

// ── Controles de demo (complementos a `@paguz/contracts` sandbox) ─────────
export const BrebFaultStateSchema = z.object({
  mode: BreBFaultModeSchema,
  remaining: z.int().min(0),
  latencyMs: z.int().min(0).nullable(),
  updatedAt: TimestampSchema.nullable(),
});
export type BrebFaultState = z.infer<typeof BrebFaultStateSchema>;

export const DemoResetResultSchema = z.object({
  scope: z.enum(['ALL', 'CUSTOMER']),
  resetAt: TimestampSchema,
  checks: z.array(z.object({ label: z.string(), ok: z.boolean() })),
});
export type DemoResetResult = z.infer<typeof DemoResetResultSchema>;

export const DemoMerchantSchema = z.object({
  alias: z.string(),
  name: z.string(),
  aliasLabel: z.string(),
});
export const DemoMerchantListSchema = z.object({ data: z.array(DemoMerchantSchema) });
